import setAuctionType from '@/api/SetAuctionType';
import EasyPassServiceClient from '@/api/EasyPassServiceClient';
import { store } from '@/appContext';
import ApiError from '@/models/ApiError';
import { getGsaleLaneId } from '@/constants/marketplace';
import EasyPassClient from './EasyPassClient';

class SavedAuctionClient {
  constructor(user) {
    this.easyPassClient = new EasyPassClient(user);
    this.easyPassServiceClient = new EasyPassServiceClient(user);
    this.rootUri = 'saved_auctions';
    this.easyPassRootUri = 'api/acvapi/v3';
    this.easyPassheaderSnake = {
      'json-naming-strategy': 'snake',
    };
  }

  getLaunchDetails({ savedAuctionId }) {
    return this.easyPassServiceClient.get(`launch/v1/${savedAuctionId}/details`, null, this.easyPassheaderSnake)
      .then((response) => response.data);
  }

  async launchAuction({
    id: savedAuctionId,
    vin,
    dealerId,
    autosell,
    floorPrice,
    purchasePrice,
    greenLight,
    selectedRunLaneKey: lane,
    laneId,
    durationInSeconds,
    selectedAddressId,
    noReservePreviewLaneValue,
    titleOption,
    auctionStartTime,
    isScheduledAuctionInScheduledLane,
    startPrice,
    userId,
    autoSendToGsale,
  }) {
    const data = {
      vin,
      dealer_id: dealerId,
      autosell,
      floor_price: floorPrice,
      purchase_price: purchasePrice,
      green_light: greenLight,
      lane,
      lane_id: laneId,
      duration_in_seconds: durationInSeconds,
      address_id: selectedAddressId,
      title_option: titleOption,
      start_price: startPrice,
      auto_send_to_g_sale: autoSendToGsale,
    };

    // Once gsale_lane_tech_enabled is on, this lane is the real Guaranteed Sale lane
    // (key 'public') rather than the fabricated no_reserve_preview row, but it still
    // launches on the same picked launch window.
    const isGsaleLane = lane === 'public' && laneId === getGsaleLaneId();

    if (lane === 'no_reserve_preview' || isGsaleLane) {
      data.auction_start_time = noReservePreviewLaneValue;
    } else if (lane === 'live_auction' || isScheduledAuctionInScheduledLane) {
      data.auction_start_time = auctionStartTime;
    }

    const shouldUseBulkEndpoint = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.launchUseBulkAuctionLaunchForSingleLaunch,
    );

    if (shouldUseBulkEndpoint) {
      data.saved_auction_id = savedAuctionId;
      data.source = 'single-launch-ui';
      data.requestor_user_id = userId;

      return this.easyPassServiceClient.post('launch/v1/bulk_auction_launch', [data], this.easyPassheaderSnake)
        .then((apiResponse) => {
          const response = apiResponse.data;
          return response.data.responses[0];
        }).catch((error) => {
          if (Array.isArray(error) && error[0].status === 400) {
            // in-depth errors
            const errorResponseMessage = error[0].response?.data?.data?.errors?.[0];

            throw new ApiError(errorResponseMessage, null);
          }
          if (error.status === 500) {
            // top level errors
            const errorResponseMessage = error.response.data?.errors?.[0];

            const errorMessage = errorResponseMessage == null
              ? 'No data returned from bulk auction launch.'
              : errorResponseMessage?.detail;

            throw new ApiError(errorMessage, null);
          }
        });
    }

    return this.easyPassServiceClient.post(`launch/v1/${savedAuctionId}/launch_auction`, data, this.easyPassheaderSnake)
      .then((response) => response.data);
  }

  async get({ savedAuctionId }) {
    const result = await this.easyPassClient.get(`${this.easyPassRootUri}/${this.rootUri}/${savedAuctionId}`);
    return result;
  }

  save(savedAuction) {
    const data = {
      vin: savedAuction.vin,
      green_light: savedAuction.greenLight,
      autosell: savedAuction.autosell,
      dealer_id: savedAuction.dealerId,
    };
    return this.easyPassClient.post(`${this.easyPassRootUri}/${this.rootUri}`, data);
  }

  getList(params) {
    return this.easyPassClient.get('bff/saved_auctions', params)
      .then((response) => setAuctionType(response, 'saved', 'saved_auctions'));
  }

  deleteSavedAuction(savedAuctionId) {
    return this.easyPassClient.delete(`${this.easyPassRootUri}/${this.rootUri}/${savedAuctionId}`);
  }
}

export default SavedAuctionClient;
